import { useFlags } from 'launchdarkly-react-client-sdk'

/**
 * Registry of flag keys used in the app. Keys are camelCased to match the React
 * SDK's default `useCamelCaseFlagKeys` behavior (LD dashboard key
 * `appex-redesign` is read here as `appexRedesign`).
 */
export const FLAGS = {
    appexRedesign: 'appexRedesign',
} as const

/**
 * Read a LaunchDarkly flag with a guaranteed default. Returns `defaultValue`
 * when LaunchDarkly is disabled, still initializing, or the flag is unknown, so
 * callers never observe `undefined`.
 *
 * @param flagKey - The camelCased flag key (see {@link FLAGS}).
 * @param defaultValue - The value to use when the flag is unavailable.
 * @returns The flag value, or `defaultValue`.
 */
export function useFlag<T>(flagKey: string, defaultValue: T): T {
    const flags = useFlags()
    const value = flags[flagKey]
    return value === undefined ? defaultValue : (value as T)
}

/**
 * Convenience hook for the redesign gate.
 *
 * @returns Whether the redesigned UI should render (default false).
 */
export function useAppexRedesignFlag(): boolean {
    return useFlag<boolean>(FLAGS.appexRedesign, false)
}
